export enum AuthSubject {
  ACCOUNT_SETTINGS = 'account_settings',
  ACCOUNT_QNS_SETTINGS = 'account_qns_settings',
  DOMAIN_SETTINGS = 'domain_settings',
  DOMAIN_QNS_SETTINGS = 'domain_qns_settings',
  USER_SETTINGS = 'user_settings',
  USER_QNS_SETTINGS = 'user_qns_settings',
  DOMAIN_LEVEL_POLICIES = 'domain_level_policies',
  DOMAIN_LEVEL_MESSAGES = 'domain_level_messages',
  // Email Protection access authorization
  EMAIL_PROTECTION_ACCESS = 'email_protection_access'
}

export interface IAuthPolicy {
  subject: AuthSubject
  value: any
  action: Array<'C' | 'R' | 'U' | 'D'>
}

export interface IRequiredPolicy {
  subject: AuthSubject
  value: any
  action: 'C' | 'R' | 'U' | 'D'
}

export const Auth = (availablePolicies: IAuthPolicy[]) => {
  const checkPolicy = (requiredPolicy: IRequiredPolicy): boolean => {
    if (availablePolicies.length === 0) {
      return false
    }

    let matchFound = false
    availablePolicies.forEach(policy => {
      if (matchFound) {
        return
      }

      const subjectMatch = policy.subject === requiredPolicy.subject

      let valueMatch = false
      if (typeof policy.value === 'string' && typeof requiredPolicy.value === 'string') {
        if (policy.value === '*' || policy.value === requiredPolicy.value) {
          valueMatch = true
        }
      } else if (typeof policy.value === 'number' && typeof requiredPolicy.value === 'number') {
        valueMatch = policy.value === requiredPolicy.value
      } else if (typeof policy.value === 'object' && typeof requiredPolicy.value === 'object') {
        valueMatch = objectMatch(policy.value, requiredPolicy.value)
      }

      const actionMatch = policy.action.includes(requiredPolicy.action)

      if (subjectMatch && valueMatch && actionMatch) {
        matchFound = true
      }
    })

    return matchFound
  }

  const objectMatch = (availableValue: Record<string, any>, requiredValue: Record<string, any>): boolean => {
    let valueMatch = true
    Object.entries(requiredValue).forEach(([key, value]) => {
      if (availableValue[key] === '*') {
        return
      }
      if (availableValue[key] !== value) {
        valueMatch = false
      }
    })
    return valueMatch
  }

  const isReadonlyAccountSettings = (accountId: string): boolean => {
    const accountSettingsAccess = checkPolicy({
      subject: AuthSubject.ACCOUNT_SETTINGS,
      value: accountId,
      action: 'U'
    })
    const accountQNSSettingsAccess = checkPolicy({
      subject: AuthSubject.ACCOUNT_QNS_SETTINGS,
      value: accountId,
      action: 'U'
    })
    return !accountSettingsAccess || !accountQNSSettingsAccess
  }

  const isReadonlyDomainSettings = (domainId: number): boolean => {
    const domainSettingsAccess = checkPolicy({
      subject: AuthSubject.DOMAIN_SETTINGS,
      value: domainId,
      action: 'U'
    })
    const domainQNSSettingsAccess = checkPolicy({
      subject: AuthSubject.DOMAIN_QNS_SETTINGS,
      value: domainId,
      action: 'U'
    })
    return !domainSettingsAccess || !domainQNSSettingsAccess
  }

  const isReadonlyUserSettings = (accountId: string, userId: string, domainId?: number): boolean => {
    const requiredValue = {
      user_id: userId,
      account_id: accountId,
      ...(domainId ? { domain_id: domainId } : {})
    }
    const userSettingsAccess = checkPolicy({
      subject: AuthSubject.USER_SETTINGS,
      value: requiredValue,
      action: 'U'
    })
    const userQNSSettingsAccess = checkPolicy({
      subject: AuthSubject.USER_QNS_SETTINGS,
      value: requiredValue,
      action: 'U'
    })
    return !userSettingsAccess || !userQNSSettingsAccess
  }

  const canReadAccountSettings = (accountId: string) => {
    const accountSettingsAccess = checkPolicy({
      subject: AuthSubject.ACCOUNT_SETTINGS,
      value: accountId,
      action: 'R'
    })
    const accountQNSSettingsAccess = checkPolicy({
      subject: AuthSubject.ACCOUNT_QNS_SETTINGS,
      value: accountId,
      action: 'R'
    })
    return accountSettingsAccess && accountQNSSettingsAccess
  }

  const canReadDomainSettings = (domainId: number): boolean => {
    const domainSettingsAccess = checkPolicy({
      subject: AuthSubject.DOMAIN_SETTINGS,
      value: domainId,
      action: 'R'
    })
    const domainQNSSettingsAccess = checkPolicy({
      subject: AuthSubject.DOMAIN_QNS_SETTINGS,
      value: domainId,
      action: 'R'
    })
    return domainSettingsAccess && domainQNSSettingsAccess
  }

  const canReadUserSettings = (accountId: string, userId: string, domainId?: number): boolean => {
    const requiredValue = {
      user_id: userId,
      account_id: accountId,
      ...(domainId ? { domain_id: domainId } : {})
    }
    const userSettingsAccess = checkPolicy({
      subject: AuthSubject.USER_SETTINGS,
      value: requiredValue,
      action: 'R'
    })
    const userQNSSettingsAccess = checkPolicy({
      subject: AuthSubject.USER_QNS_SETTINGS,
      value: requiredValue,
      action: 'R'
    })
    return userSettingsAccess && userQNSSettingsAccess
  }

  const canManageDomainPolicies = (domainId: number): boolean =>
    checkPolicy({
      subject: AuthSubject.DOMAIN_LEVEL_POLICIES,
      value: { domain_id: domainId },
      action: 'U'
    })

  const canReadDomainPolicies = (domainId: number): boolean =>
    checkPolicy({
      subject: AuthSubject.DOMAIN_LEVEL_POLICIES,
      value: { domain_id: domainId },
      action: 'R'
    })

  const canViewDomainMessages = (domainId: number): boolean =>
    checkPolicy({
      subject: AuthSubject.DOMAIN_LEVEL_MESSAGES,
      value: { domain_id: domainId },
      action: 'R'
    })

  /**
   * Check if user has Email Protection access for a specific account.
   * Used by Email Log to filter the account switcher list on the UI side.
   */
  const hasEmailProtectionAccess = (bccAccountId: string): boolean =>
    checkPolicy({
      subject: AuthSubject.EMAIL_PROTECTION_ACCESS,
      value: { bcc_account_id: bccAccountId },
      action: 'R'
    })

  return Object.freeze({
    availablePolicies,
    checkPolicy,
    isReadonlyAccountSettings,
    isReadonlyDomainSettings,
    isReadonlyUserSettings,
    canReadAccountSettings,
    canReadDomainSettings,
    canReadUserSettings,
    canManageDomainPolicies,
    canReadDomainPolicies,
    canViewDomainMessages,
    hasEmailProtectionAccess
  })
}

export type IAuth = ReturnType<typeof Auth>
